// apps/web/src/pages/RegisterPage.tsx
// Phase 1 实现 — 注册页面，含表单验证

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { registerApi } from "@/api/auth";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import type { AuthResponse } from "@examfast/shared-types";

const schema = z.object({
  email: z.string().email("请输入有效的邮箱"),
  password: z.string().min(8, "密码至少 8 位").max(64, "密码最多 64 位"),
  confirmPassword: z.string().min(1, "请确认密码"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "两次密码不一致",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof schema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);

  const mutation = useMutation({
    mutationFn: registerApi,
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      toast.success("注册成功");
      navigate("/", { replace: true });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "注册失败");
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 bg-white">
      {/* 标题 */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-primary">创建账号</h1>
        <p className="text-gray-400 text-sm mt-1">注册 Exam-Fast，开始刷题</p>
      </div>

      {/* 表单 */}
      <form
        onSubmit={handleSubmit((vals) => mutation.mutate(vals))}
        className="space-y-4"
        noValidate
      >
        {/* 邮箱 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
          <input
            type="email"
            autoComplete="email"
            {...register("email")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="text-danger text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* 密码 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
          <input
            type="password"
            autoComplete="new-password"
            {...register("password")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="至少 8 位"
          />
          {errors.password && (
            <p className="text-danger text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        {/* 确认密码 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">确认密码</label>
          <input
            type="password"
            autoComplete="new-password"
            {...register("confirmPassword")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="再次输入密码"
          />
          {errors.confirmPassword && (
            <p className="text-danger text-xs mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* 提交按钮 */}
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold
                     rounded-xl transition-colors disabled:opacity-50 btn-tap"
        >
          {mutation.isPending ? "注册中..." : "注册"}
        </button>
      </form>

      {/* 去登录 */}
      <p className="text-center text-sm text-gray-400 mt-6">
        已有账号？
        <Link to="/login" className="text-primary font-medium ml-1">
          去登录
        </Link>
      </p>
    </div>
  );
}
